import { motion } from 'framer-motion';
import { useState } from 'react';
import { Check, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Link } from 'react-router-dom';

export default function PricingPage() {
  const [billingCycle, setBillingCycle] = useState('monthly');

  const plans = [
    {
      name: 'Standard',
      studentLimit: 'Up to 200 Students',
      monthlyPrice: 1000,
      yearlyPrice: 12000,
      monthlyOriginalPrice: 1500,
      yearlyOriginalPrice: 18000,
      monthlyPricingBasis: '₹5 per Student / Month',
      yearlyPricingBasis: '₹60 per Student / Year (₹5 per Student / Month)',
      features: [
        'Student Management',
        'Admission Management',
        'Attendance Management',
        'Fee Management',
        'Exam Management',
        'Holiday Management',
        'Homework Management',
        'Report Cards & Certificates',
        'Parent & Student Portal',
        'Mobile App Access',
        'Staff Management',
        'Basic Reports & Analytics',
        'Email Support',
      ],
      popular: false,
    },
    {
      name: 'Premium',
      studentLimit: 'Up to 200 Students',
      monthlyPrice: 200 * 8,
      yearlyPrice: 200 * 96,
      monthlyOriginalPrice: 200 * 12,
      yearlyOriginalPrice: 200 * 144,
      monthlyPricingBasis: '₹8 per Student / Month',
      yearlyPricingBasis: '₹96 per Student / Year (₹8 per Student / Month)',
      features: [
        'Everything in Standard',
        'Transport Management',
        'WhatsApp Integration',
        'Online Admission Management',
        'Advanced Reports & Analytics',
        'Fee Reminders via WhatsApp',
        'Priority Support',
      ],
      popular: true,
    },
  ];

  const faqs = [
    {
      question: 'Is there a free trial available?',
      answer:
        'Yes! We offer a 14-day free trial with full access to all features. No credit card required.',
    },
    {
      question: 'Can I upgrade or downgrade my plan?',
      answer:
        'Absolutely. You can change your plan at any time. The changes will be reflected in your next billing cycle.',
    },
    {
      question: 'What payment methods do you accept?',
      answer:
        'We accept all major credit/debit cards, UPI, net banking, and can also provide invoice-based payment for annual plans.',
    },
    {
      question: 'Is my data secure?',
      answer:
        'Yes, we use bank-level encryption and store data on secure cloud servers with daily backups. Your data is completely safe.',
    },
    {
      question: 'Do you provide training?',
      answer:
        'Yes, we provide comprehensive onboarding and training for your staff. Training materials and video tutorials are also available.',
    },
  ];

  return (
    <div data-testid="pricing-page" className="pt-20">
      <section className="py-24 px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12">
            <h1 className="text-5xl sm:text-6xl tracking-tighter font-black text-navy mb-6 font-heading">
              {billingCycle === 'yearly' ? 'Annual Pricing Plans' : 'Pricing Plans'}
            </h1>
            <p className="text-base leading-relaxed text-slate-600 max-w-2xl mx-auto mb-8">
              Choose the perfect plan for your institution. All plans include
              mobile apps and regular updates.
            </p>

            <Tabs
              value={billingCycle}
              onValueChange={setBillingCycle}
              className="inline-flex"
              data-testid="billing-cycle-tabs"
            >
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="monthly" data-testid="monthly-tab">
                  Monthly
                </TabsTrigger>
                <TabsTrigger value="yearly" data-testid="yearly-tab">
                  Yearly
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16 max-w-4xl mx-auto">
            {plans.map((plan, index) => (
              <motion.div
                key={index}
                data-testid={`pricing-card-${plan.name.toLowerCase()}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className={`relative bg-white rounded-2xl border-2 p-8 pricing-card ${
                  plan.popular
                    ? 'border-emerald featured shadow-lg'
                    : 'border-slate-200'
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-emerald text-white px-4 py-1 rounded-full text-sm font-semibold">
                      Most Popular
                    </span>
                  </div>
                )}

                <div className="mb-6">
                  <h3 className="text-2xl font-bold text-navy mb-2 font-heading">
                    {plan.name}
                  </h3>
                  <p className="text-sm text-slate-600">{plan.studentLimit}</p>
                </div>

                <div className="mb-6">
                  <div className="flex items-baseline flex-wrap gap-x-3 gap-y-1">
                    <span className="text-xl text-slate-400 line-through font-heading">
                      ₹
                      {(billingCycle === 'monthly'
                        ? plan.monthlyOriginalPrice
                        : plan.yearlyOriginalPrice
                      ).toLocaleString()}
                    </span>
                    <span className="text-4xl font-black text-navy font-heading">
                      ₹
                      {billingCycle === 'monthly'
                        ? plan.monthlyPrice.toLocaleString()
                        : plan.yearlyPrice.toLocaleString()}
                    </span>
                    <span className="text-slate-500">
                      / {billingCycle === 'monthly' ? 'Month' : 'Year'}
                    </span>
                  </div>
                </div>

                <Link to="/register">
                  <Button
                    data-testid={`start-trial-${plan.name.toLowerCase()}`}
                    className={`w-full mb-6 font-semibold ${
                      plan.popular
                        ? 'bg-emerald hover:bg-emerald-light text-white'
                        : 'bg-slate-100 hover:bg-slate-200 text-navy'
                    }`}
                  >
                    Start Free Trial
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>

                <p className="text-sm font-semibold text-navy mb-3">Features</p>
                <div className="space-y-3 mb-6">
                  {plan.features.map((feature, idx) => (
                    <div
                      key={idx}
                      data-testid={`feature-${index}-${idx}`}
                      className="flex items-start space-x-3"
                    >
                      <Check className="h-5 w-5 text-emerald flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-slate-700">{feature}</span>
                    </div>
                  ))}
                </div>

                <p className="text-xs text-slate-500 border-t border-slate-100 pt-4">
                  <span className="font-semibold text-slate-600">Pricing Basis: </span>
                  {billingCycle === 'monthly'
                    ? plan.monthlyPricingBasis
                    : plan.yearlyPricingBasis}
                </p>
              </motion.div>
            ))}
          </div>

          <div className="text-center">
            <p className="text-slate-600 mb-4">
              Need a custom plan for your institution?
            </p>
            <Link to="/contact">
              <Button
                data-testid="talk-expert-button"
                variant="outline"
                className="border-2 border-navy text-navy hover:bg-slate-50 font-semibold"
              >
                Talk to Our Expert
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <section
        data-testid="faq-section"
        className="py-24 bg-slate-50 px-6 md:px-12 lg:px-24"
      >
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl sm:text-4xl tracking-tight font-bold text-navy mb-12 text-center font-heading">
            Frequently Asked Questions
          </h2>
          <div className="space-y-6">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                data-testid={`faq-item-${index}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white p-6 rounded-2xl border border-slate-200"
              >
                <h3 className="text-lg font-semibold text-navy mb-2">
                  {faq.question}
                </h3>
                <p className="text-slate-600">{faq.answer}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
